import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import type { JSONCompatible } from '@common/types';
import type { TemplateFormField } from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { Box, Stack, useMediaQuery, useTheme } from '@mui/material';
import type { ContactBlockEntryErrors } from '@/pages/QuoteIntakeClientView/IntakeFormView/contactValidation';
import FormFieldInput from './FormFieldInput';
import FormFieldLabel from './FormFieldLabel';
import { formFieldContainerStyle } from './formFieldStyles';

export type FormComponentField = Pick<TemplateFormField, 'title' | 'questionSchema'> &
    Partial<
        Pick<
            TemplateFormField,
            'questionValidationSchema' | 'displayOrder' | 'isRequiredForFullAutomation'
        >
    >;

export interface FormComponentProps {
    fieldObj: FormComponentField;
    lawFirmId: string | number;
    isRenderFrenchStaticValue?: boolean;
    language?: TemplateFormLanguage;
    value?: JSONCompatible;
    labelStyles?: object;
    isError?: boolean;
    helper?: string;
    tabindex?: string | number;
    containerPadding?: string;
    disabled?: boolean;
    readOnly?: boolean;
    formGroupIndex?: number;
    shouldRenderAsterisk?: boolean;
    shouldRenderCritical?: boolean;
    typeOfIdValue?: string;
    isCriticalQuestion?: boolean;
    showSelectBoxValue?: boolean;
    /**
     * When true, also treat `fieldObj.isRequiredForFullAutomation` as requiring an asterisk.
     * Callers should gate this the same way they gate automation-required validation.
     */
    includeFieldAutomationRequired?: boolean;
    /** Per-entry errors for `contactInfoSection` questions. */
    entryErrors?: ContactBlockEntryErrors[];
    onFieldValueChange?: (name: string, value: JSONCompatible) => void;
}

export default function FormComponent({
    fieldObj,
    lawFirmId,
    isRenderFrenchStaticValue = false,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
    value,
    labelStyles,
    isError,
    helper,
    tabindex,
    shouldRenderAsterisk = true,
    showSelectBoxValue,
    disabled,
    readOnly,
    formGroupIndex,
    containerPadding,
    typeOfIdValue,
    shouldRenderCritical = true,
    isCriticalQuestion,
    includeFieldAutomationRequired = false,
    entryErrors,
    onFieldValueChange,
}: FormComponentProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // Prefer explicit language; keep legacy isRenderFrenchStaticValue for V1 / builder callers
    const resolvedLanguage: TemplateFormLanguage =
        language === 'fr' || isRenderFrenchStaticValue ? 'fr' : 'en';

    const questionSchema = fieldObj.questionSchema;

    const customConditionalProps = {
        ...(isError && {
            'data-is-error': fieldObj.displayOrder || true,
            'data-formgroup-index': formGroupIndex,
        }),
    };

    return (
        <Box {...customConditionalProps} sx={{ width: '100%' }}>
            <Box
                sx={{
                    ...formFieldContainerStyle,
                    alignItems: 'center',
                    padding: containerPadding
                        ? containerPadding
                        : isMobile
                          ? '0.5rem 0.5rem'
                          : '0.75rem 1.5rem',
                    boxSizing: 'border-box',
                    width: '100%',
                }}
            >
                <Stack sx={{ width: '100%' }}>
                    <FormFieldLabel
                        schema={questionSchema}
                        label={fieldObj.title}
                        validation={fieldObj.questionValidationSchema}
                        labelStyles={labelStyles}
                        disabled={disabled}
                        shouldRenderAsterisk={shouldRenderAsterisk}
                        shouldRenderCritical={shouldRenderCritical}
                        isCriticalQuestion={isCriticalQuestion}
                        includeFieldAutomationRequired={includeFieldAutomationRequired}
                        isRequiredForFullAutomation={fieldObj.isRequiredForFullAutomation}
                        language={resolvedLanguage}
                    />
                    <FormFieldInput
                        schema={questionSchema}
                        validation={fieldObj.questionValidationSchema}
                        value={value}
                        isError={isError}
                        helper={helper}
                        tabindex={tabindex}
                        disabled={disabled}
                        readOnly={readOnly}
                        showSelectBoxValue={showSelectBoxValue}
                        typeOfIdValue={typeOfIdValue}
                        lawFirmId={lawFirmId}
                        language={resolvedLanguage}
                        entryErrors={entryErrors}
                        onFieldValueChange={onFieldValueChange}
                    />
                </Stack>
            </Box>
        </Box>
    );
}
